import { FeedbackError } from '@/components/Feedback';
import config from '@/Constants';
import { useAuth } from '@/hooks/Authentication/auth';
import { DiscussionTopic, TopicComment } from '@/hooks/Feedback/interfaces';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { InfiniteData, useMutation, useQueryClient } from 'react-query';
import { v4 as uuidV4 } from 'uuid';

import {
  createFeedbackComment,
  TopicCommentPostData,
} from './services/createFeedbackComment';
import { discussionTopicsKey } from './useFeedbackQuery';

const mutationKey = config.DISCUSSION_COMMENTS_URL;

export function useFeedbackCommentMutation() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const mutation = useMutation(createFeedbackComment, {
    onError(error) {
      FeedbackError({
        mainText: t('error.warning'),
        subText:
          error instanceof Error ? error.message : t('error.unexpectedError'),
      });
    },
    onSettled(data, error, variables) {
      const queryKey = [mutationKey, variables.discussionTopicId];
      // Um novo comentário só afeta a página mais recente já carregada;
      // páginas mais antigas (comentários carregados via scroll infinito)
      // não precisam ser refeitas.
      queryClient.invalidateQueries<TopicComment[]>(queryKey, {
        refetchPage: (page, index, allPages) => index === allPages.length - 1,
      });
    },
    onMutate(variables) {
      const { content, discussionTopicId, media } = variables;
      const optimisticComment: TopicComment = {
        authorId: `${user?.id}`,
        authorName: `${user?.full_name}`,
        content: content ?? '',
        id: `${uuidV4()}-temp`,
        createdAt: new Date(),
        topicActivityType: 'comment',
        medias: media?.map((m, i) => ({
          id: `${uuidV4()}-temp-media-${i}`,
          content: m.content,
          fileName: m.fileName,
          fileSize: m.fileSize,
          position: m.position,
          fileUrl: '',
          hasMedia: true,
          mediaExternalProcessed: true,
          mediaProcessed: false,
          hasErrorInMedia: false,
        })),
      };
      const queryKey = [mutationKey, discussionTopicId];

      queryClient.setQueryData<InfiniteData<TopicComment[]>>(
        queryKey,
        (old = { pages: [], pageParams: [] }) => {
          const pages = [...old.pages];
          const lastIndex = pages.length - 1;
          if (lastIndex < 0) {
            pages.push([optimisticComment]);
          } else {
            pages[lastIndex] = [...pages[lastIndex], optimisticComment];
          }
          return { ...old, pages };
        },
      );
    },
  });

  const createComment = React.useCallback(
    async (params: TopicCommentPostData) => {
      try {
        await mutation.mutateAsync(
          {
            ...params,
          },
          {
            onSuccess(data, variables) {
              const queryData = queryClient.getQueryData<DiscussionTopic>([
                discussionTopicsKey,
                variables.discussionTopicId,
              ]);

              if (queryData) {
                queryClient.setQueryData<DiscussionTopic>(
                  [discussionTopicsKey, variables.discussionTopicId],
                  {
                    ...queryData,
                    lastComment: data,
                    commentsCount: queryData.commentsCount + 1,
                  },
                );
              }
            },
          },
        );
      } catch {
        // erro já exibido via onError do mutation
      }
    },
    [mutation, queryClient],
  );

  const { isError, isLoading, data, reset, isSuccess, error } = mutation;
  return {
    createComment,
    isError,
    isLoading,
    data,
    reset,
    isSuccess,
    error,
  };
}
